import * as z from 'zod/v4';

import { drawNumberSchema } from '../draw/entry.js';
import { currencySchema, winDivisionSchema } from '../lib/entry.js';
import { EVENT_TYPE_VARIANTS, eventNumberSchema, eventTypeIdSchema, type matchStateSchema } from '../sport/entry.js';
import { type DrawForecast } from './drawForecast.js';
import { outcomeSchema } from './outcomeSchema.js';
import { scoreSchema } from './scoreSchema.js';

const resultWinDivisionSchema = z
  .number()
  .nonnegative()
  .transform((n) => n + 1);

// TODO: check with responsible team if eventTypeId can be added, this will work for now
// but not when we add Bomben / Matchen or Challenge
const defaultEventTypeIdSchema = z.number().default(EVENT_TYPE_VARIANTS.EVENT_1X2);

const resultOutcomeSchema = z
  .object({
    outcomeScore: z.object({
      home: z.unknown(),
      away: z.unknown(),
    }),
    outcome: z.string(),
    eventTypeId: defaultEventTypeIdSchema,
  })
  .transform(({ outcomeScore, ...schema }) => ({
    ...schema,
    ...outcomeScore,
  }));

const resultOutcomeScoreSchema = z.object({
  outcomeScore: scoreSchema,
});

const eventSchema = z
  .object({
    eventNumber: eventNumberSchema,
    eventTypeId: defaultEventTypeIdSchema.pipe(eventTypeIdSchema),
    cancelled: z.boolean(),
  })
  .and(
    resultOutcomeSchema.pipe(outcomeSchema).transform((outcome) => {
      return {
        outcome,
      };
    })
  )
  .and(
    resultOutcomeScoreSchema.transform(({ outcomeScore: result }) => ({
      result,
    }))
  )
  .transform(({ eventTypeId: eventType, cancelled: isCancelled, ...schema }) => {
    // When draw is in result state, match state will be considered to be 'Finished'
    const matchResult: z.infer<typeof matchStateSchema> = { client: 'Finished' };

    return {
      ...schema,
      eventType,
      matchResult,
      isCancelled,
    };
  });

const distributionSchema = z
  .object({
    winDiv: resultWinDivisionSchema.pipe(winDivisionSchema),
    winners: z.number(),
    odds: currencySchema,
  })
  .transform(({ winDiv: winDivision, odds: winValue, ...schema }) => ({
    ...schema,
    winDivision,
    winValue,
  }));

export const drawResultSchema = z
  .object({
    drawNumber: drawNumberSchema,
    currentNetSale: currencySchema,
    events: z.array(eventSchema),
    cancelled: z.boolean(),
    distribution: z.array(distributionSchema),
  })
  .transform(({ currentNetSale: netSale, cancelled: isCancelled, distribution: winDivisions, events, ...schema }) => {
    // When draw is in result state, state will always be 'Finished'
    const drawState: DrawForecast['drawState'] = { client: 'Finished' };

    return {
      ...schema,
      drawState,
      drawResults: events,
      netSale,
      isCancelled,
      winDivisions,
    } satisfies Omit<DrawForecast, 'multiForecast'>;
  });

export type DrawResult = z.infer<typeof drawResultSchema>;
